import { createBrowserRouter, Navigate } from 'react-router-dom';
import Routes from './Routes.ts';
import LoginPage from '@/pages/LoginPage.tsx';
import SandboxPage from '@/pages/SandboxPage.tsx';
import { transformRouterConfig } from '@/router/transformRouter.tsx';
import RegisterPage from '@/pages/RegisterPage.tsx';
import Default from '@/layouts/Default/Default.tsx';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage.tsx';
import ResetPasswordPage from '@/pages/ResetPasswordPage.tsx';
import DashboardRedirect from '@/components/DashboardRedirect.tsx';
import SimpleCenter from '@/layouts/SimpleCenter/SimpleCenter.tsx';
import ConfirmEmailPage from '@/pages/ConfirmEmailPage.tsx';
import PaymentSelectPage from '@/pages/PaymentSelectPage.tsx';
import DataTablePage from '@/pages/DataTablePage.tsx';
import InputsPage from '@/pages/InputsPage.tsx';
import { LayoutInternal } from '$/views/emails/layout.tsx';
import PasswordChangedConfirmationPage from '@/pages/PasswordChangedConfirmationPage.tsx';
import EmailChangedConfirmationPage from '@/pages/EmailChangedConfirmationPage.tsx';
import OnboardingPage from '@/pages/OnboardingPage.tsx';
import BusinessOnboardingPage from '@/pages/BusinessOnboardingPage.tsx';
import SettingsPage from '@/pages/SettingsPage.tsx';
import FaqPage from '@/pages/FaqPage.tsx';
import TermsPage from '@/pages/TermsPage.tsx';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage.tsx';
import CarersPage from '@/pages/CarersPage.tsx';
import CarerDetailsPage from '@/pages/CarerDetailsPage.tsx';
import BookingsPage from '@/pages/BookingsPage.tsx';
import BookingDetailsFullPage from '@/pages/BookingDetailsFullPage.tsx';
import IntroCallsPage from '@/pages/IntroCallsPage.tsx';
import IntroCallDetailsFullPage from '@/pages/IntroCallDetailsFullPage.tsx';
import DocumentsPage from '@/pages/DocumentsPage.tsx';
import MessagesPage from '@/pages/MessagesPage.tsx';
import AdminFamiliesPage from '@/pages/admin/AdminFamiliesPage.tsx';
import AdminFamilyDetailsPage from '@/pages/admin/AdminFamilyDetailsPage.tsx';
import AdminCarersPage from '@/pages/admin/AdminCarersPage.tsx';
import AdminCarerDetailsPage from '@/pages/admin/AdminCarerDetailsPage.tsx';
import AdminRequestsPage from '@/pages/admin/AdminRequestsPage.tsx';
import AdminBookingsPage from '@/pages/admin/AdminBookingsPage.tsx';

const router = createBrowserRouter(
  transformRouterConfig([
    {
      path: '/',
      element: <Default.Layout />,
      middleware: ['loggedIn', 'familyOnboarding', 'businessOnboarding'],
      children: [
        {
          path: Routes.DASHBOARD,
          element: <DashboardRedirect />,
        },
        {
          path: Routes.SANDBOX,
          element: <SandboxPage />,
        },
        {
          path: Routes.DATA_TABLE,
          element: <DataTablePage />,
        },
        {
          path: Routes.INPUTS,
          element: <InputsPage />,
        },
        {
          path: Routes.CARERS,
          element: <CarersPage />,
        },
        {
          path: Routes.CARER_DETAILS,
          element: <CarerDetailsPage />,
        },
        {
          path: Routes.MESSAGES,
          element: <MessagesPage />,
        },
        {
          path: Routes.MESSAGES_WITH_USER,
          element: <MessagesPage />,
        },
        {
          path: Routes.BOOKING,
          element: <BookingsPage />,
        },
        {
          path: Routes.BOOKING_DETAILS,
          element: <BookingDetailsFullPage />,
        },
        {
          path: Routes.INTRO_CALLS,
          element: <IntroCallsPage />,
        },
        {
          path: Routes.INTRO_CALL_DETAILS,
          element: <IntroCallDetailsFullPage />,
        },
        {
          path: Routes.DOCUMENTS,
          element: <DocumentsPage />,
        },
        {
          path: Routes.FAQ,
          element: <FaqPage />,
        },
        {
          path: Routes.SETTINGS,
          element: <SettingsPage />,
        },
        // Admin routes
        {
          path: Routes.ADMIN_FAMILIES,
          element: <AdminFamiliesPage />,
        },
        {
          path: Routes.ADMIN_FAMILY_DETAILS,
          element: <AdminFamilyDetailsPage />,
        },
        {
          path: Routes.ADMIN_CARERS,
          element: <AdminCarersPage />,
        },
        {
          path: Routes.ADMIN_CARER_DETAILS,
          element: <AdminCarerDetailsPage />,
        },
        {
          path: Routes.ADMIN_REQUESTS,
          element: <AdminRequestsPage />,
        },
        {
          path: Routes.ADMIN_BOOKINGS,
          element: <AdminBookingsPage />,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['notLoggedIn'],
      children: [
        {
          path: Routes.FORGOT_PASSWORD,
          element: <ForgotPasswordPage />,
        },
        {
          path: Routes.LOGIN,
          element: <LoginPage />,
        },
        {
          path: Routes.REGISTER,
          element: <RegisterPage />,
        },
        {
          path: Routes.CONFIRM_EMAIL,
          element: <ConfirmEmailPage />,
        },
      ],
    },
    // SimpleCenter layout
    {
      path: '/',
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.RESET_PASSWORD,
          element: <ResetPasswordPage />,
        },
        {
          path: Routes.PASSWORD_CHANGED_CONFIRMATION,
          element: <PasswordChangedConfirmationPage />,
        },
        {
          path: Routes.EMAIL_CHANGED_CONFIRMATION,
          element: <EmailChangedConfirmationPage />,
        },
        {
          path: Routes.TERMS,
          element: <TermsPage />,
        },
        {
          path: Routes.PRIVACY_POLICY,
          element: <PrivacyPolicyPage />,
        },
      ],
    },
    // SimpleCenter logged in
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.PAYMENT_SELECT,
          element: <PaymentSelectPage />,
        },
      ],
    },
    // Family member onboarding (no nav)
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn', 'familyOnboarding'],
      children: [
        {
          path: Routes.ONBOARDING,
          element: <OnboardingPage />,
        },
      ],
    },
    // Business provider onboarding (no nav)
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn', 'businessOnboarding'],
      children: [
        {
          path: Routes.BUSINESS_ONBOARDING,
          element: <BusinessOnboardingPage />,
        },
      ],
    },
    {
      path: Routes.EMAIL_TEMPLATE,
      element: (
        <LayoutInternal
          appUrl={import.meta.env.VITE_APP_URL}
          appName={'caretailor'}
          logo={`${import.meta.env.VITE_APP_URL}/assets/logo.svg`}
        >
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ab animi atque possimus, quaerat quasi tempore
          totam! Architecto assumenda cumque deleniti dolor expedita facilis illo minima nihil quidem soluta? Hic,
          laboriosam!
        </LayoutInternal>
      ),
    },
    {
      path: '*',
      element: <Navigate to={Routes.DASHBOARD} />,
    },
  ])
);

export default router;
